import { useCallback, useMemo } from "react";
import { useSessionStorage } from "./storage/useSessionStorage";

interface UsePaginationOptions {
  page?: number; // starting page
  min?: number;
  max?: number | null;
}

const getInitialPage = (
  max: number | null,
  min: number,
  initialPage: number | undefined,
) => {
  let initial = initialPage || 1;
  if (initial < min) initial = min;
  if (max && initial > max) initial = max;
  return initial;
};

export const PAGE_SIZE = 20;
export interface usePaginationResult<T> {
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  status: "loading" | "error" | "success" | "idle";
  error: null | unknown;
  isFetching: boolean;
  data: T[] | undefined;
  count: number;
}

export const usePagination = (
  storageKey: string,
  options?: UsePaginationOptions,
) => {
  // Core State
  const max = useMemo(() => options?.max || null, [options]);
  const min = useMemo(() => options?.min || 1, [options]);

  const [page, _setPage] = useSessionStorage<number>(
    storageKey,
    getInitialPage(max, min, options?.page),
    { removeOnUnmount: true },
  );

  // Utility Methods
  const setPage = useCallback(
    (targetPage: number) => {
      let n = targetPage;

      if (n < min) n = min;
      if (max && n > max) n = max;

      _setPage(n);
    },
    [min, max, _setPage],
  );

  const nextPage = useCallback(() => {
    setPage(page + 1);
  }, [page, setPage]);

  const prevPage = useCallback(() => {
    setPage(page - 1);
  }, [page, setPage]);

  // Consumer API
  return { page, setPage, nextPage, prevPage };
};
